import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  Lightbulb, 
  BarChart3, 
  PieChart,
  Search,
  ChevronDown,
  ChevronUp,
  Frown,
  Meh,
  Smile,
  Calendar,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Eye,
  Target,
  Award,
  X
} from 'lucide-react';
import { useToast } from './ToastContainer';
import { useAuth } from '../contexts/JWTAuthContext';
import api from '../utils/api';
import './Analysis.css';

const Analysis = () => {
  const toast = useToast();
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  
  // Analysis data states
  const [reviewAnalyses, setReviewAnalyses] = useState([]);
  const [insightProfiles, setInsightProfiles] = useState([]);
  const [currentInsight, setCurrentInsight] = useState(null);
  
  // HYBRID APPROACH: Separate state for overview and review details
  // Overview data (complete statistics)
  const [overviewData, setOverviewData] = useState({
    total_reviews: 0,
    analyzed_reviews: 0,
    unanalyzed_count: 0,
    skipped_reviews: 0,
    analyzable_reviews: 0,
    analysis_completion_percentage: 0,
    avg_sentiment_score: 0,
    urgent_reviews: 0
  });
  
  // Review details data (paginated)
  const [reviewDetailsData, setReviewDetailsData] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [, setAnalysisProgress] = useState({ 
    current: 0, 
    total: 0, 
    status: '',
    batch: 0,
    totalBatches: 0,
    errors: 0
  });
  const [lastAnalysisRun, setLastAnalysisRun] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Pagination state for large datasets
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default to 10 reviews per page
  const [totalPages, setTotalPages] = useState(1);
  const [totalAnalyses, setTotalAnalyses] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [emotionFilter, setEmotionFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Insight creation states
  const [showCreateInsight, setShowCreateInsight] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [insightForm, setInsightForm] = useState({
    profile_name: '',
    date_range: 'last_week',
    custom_start: '',
    custom_end: '',
    custom_criteria: [],
    ai_auto_detect: true
  });
  const [newCriteria, setNewCriteria] = useState('');

  // Fetch branches on component mount
  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when branch changes
  useEffect(() => {
    if (selectedBranch) {
      console.log('🔄 useEffect triggered for branch:', selectedBranch);
      fetchOverviewData(); // Always fetch overview data
      fetchInsightProfiles();
      if (activeTab === 'reviews') {
        fetchReviewDetails(); // Only fetch reviews if on reviews tab
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  // Fetch reviews when switching to reviews tab
  useEffect(() => {
    if (selectedBranch && activeTab === 'reviews' && reviewDetailsData.length === 0) {
      fetchReviewDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBranch]);

  // Fetch reviews when page changes
  useEffect(() => {
    if (selectedBranch && activeTab === 'reviews') {
      fetchReviewDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Fetch reviews when filters change
  useEffect(() => {
    if (selectedBranch && activeTab === 'reviews') {
      console.log('🔍 Filter state changed, fetching new data...', {
        dateFilter, sentimentFilter, emotionFilter, urgencyFilter, customDateRange
      });
      setCurrentPage(1); // Reset to first page when filters change
      setReviewDetailsData([]); // Clear current data to show loading
      fetchReviewDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, sentimentFilter, emotionFilter, urgencyFilter, customDateRange]);

  // Monitor reviewAnalyses state changes
  useEffect(() => {
    console.log('🔍 reviewAnalyses state changed:', reviewAnalyses.length, 'analyses');
    if (reviewAnalyses.length > 0) {
      console.log('🔍 First analysis in state:', reviewAnalyses[0]);
      console.log('🔍 First analysis reviews in state:', reviewAnalyses[0]?.reviews);
      console.log('🔍 Reviewer name in state:', reviewAnalyses[0]?.reviews?.reviewer_name);
      console.log('🔍 Published date in state:', reviewAnalyses[0]?.reviews?.published_at_date);
    }
  }, [reviewAnalyses]);

  // Monitor reviewDetailsData state changes
  useEffect(() => {
    console.log('🔍 reviewDetailsData state changed:', reviewDetailsData.length, 'analyses');
    if (reviewDetailsData.length > 0) {
      console.log('🔍 First analysis in reviewDetailsData:', reviewDetailsData[0]);
      console.log('🔍 Reviewer name in reviewDetailsData:', reviewDetailsData[0]?.reviews?.reviewer_name);
      console.log('🔍 Published date in reviewDetailsData:', reviewDetailsData[0]?.reviews?.published_at_date);
    }
  }, [reviewDetailsData]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/api/branches');
      const data = response.data;
      if (data.success && data.branches.length > 0) {
        setBranches(data.branches);
        setSelectedBranch(data.branches[0].id); // Auto-select first branch
      } else {
        toast.showError('No branches found');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.showError('Failed to fetch branches');
    }
  };

  // HYBRID APPROACH: Fetch complete overview statistics
  const fetchOverviewData = async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    try {
      console.log('📊 Fetching OVERVIEW data (complete statistics)...');
      
      // Fetch all the statistics we need in parallel
      const [
        unanalyzedResponse,
        analyzedResponse,
        analyzableResponse,
        totalReviewsResponse,
        urgentCountResponse
      ] = await Promise.all([
        api.get(`/api/ai/unanalyzed-count?branch_id=${selectedBranch}`),
        api.get(`/api/ai/analyzed-count?branch_id=${selectedBranch}`),
        api.get(`/api/ai/analyzable-count?branch_id=${selectedBranch}`),
        api.get(`/api/reviews?branchIds=${selectedBranch}&limit=1`),
        api.get(`/api/ai/review-analysis?branch_id=${selectedBranch}&urgency_flag=true&limit=10000`) // Get all urgent reviews
      ]);

      // Get the real counts
      const totalReviews = totalReviewsResponse.data.total || 0;
      const unanalyzedCount = unanalyzedResponse.data.count || 0;
      const analyzedCount = analyzedResponse.data.count || 0;
      const analyzableCount = analyzableResponse.data.count || 0;
      const urgentCount = urgentCountResponse.data.success ? (urgentCountResponse.data.analyses || []).length : 0;
      
      // Debug logging
      console.log('📊 Overview data received:', {
        totalReviews,
        unanalyzedCount,
        analyzedCount,
        analyzableCount,
        urgentCount,
        branch: selectedBranch
      });
      console.log('📊 API responses:', {
        unanalyzedResponse: unanalyzedResponse.data,
        analyzedResponse: analyzedResponse.data,
        analyzableResponse: analyzableResponse.data,
        totalReviewsResponse: totalReviewsResponse.data
      });
      
      // Calculate empty text reviews
      const emptyTextCount = unanalyzedCount - analyzableCount;
      const completionPercentage = totalReviews > 0 ? ((analyzedCount / totalReviews) * 100).toFixed(1) : 0;

      // Calculate average sentiment from a sample of analyzed reviews
      const sentimentResponse = await api.get(`/api/ai/review-analysis?branch_id=${selectedBranch}&limit=1000`);
      let avgSentiment = 0;
      if (sentimentResponse.data.success) {
        const analyses = sentimentResponse.data.analyses || [];
        const sentimentScores = analyses.map(a => a.sentiment_score).filter(score => score !== null && score !== undefined);
        avgSentiment = sentimentScores.length > 0 
          ? sentimentScores.reduce((sum, score) => sum + score, 0) / sentimentScores.length 
          : 0;
      }

      // Update overview data with complete statistics
      setOverviewData({
        total_reviews: totalReviews,
        analyzed_reviews: analyzedCount,
        unanalyzed_count: analyzableCount,
        skipped_reviews: emptyTextCount,
        analyzable_reviews: analyzableCount + analyzedCount,
        analysis_completion_percentage: completionPercentage,
        avg_sentiment_score: avgSentiment,
        urgent_reviews: urgentCount
      });

      console.log('📊 Overview data updated:', {
        total_reviews: totalReviews,
        analyzed_reviews: analyzedCount,
        urgent_reviews: urgentCount,
        completion_percentage: completionPercentage
      });

    } catch (error) {
      console.error('Error fetching overview data:', error);
      toast.showError('Failed to fetch overview data');
    } finally {
      setLoading(false);
    }
  };

  // HYBRID APPROACH: Fetch paginated review details with filtering
  const fetchReviewDetails = async () => {
    if (!selectedBranch) return;
    
    setLoadingReviews(true);
    try {
      console.log(`📄 Fetching REVIEW DETAILS (filtered): page=${currentPage}, pageSize=${pageSize}`);
      console.log(`🔍 Current filters:`, { dateFilter, sentimentFilter, emotionFilter, urgencyFilter, customDateRange });
      
      // Build filter parameters for the filtered API
      const filterParams = new URLSearchParams({
        branch_id: selectedBranch,
        page: currentPage,
        pageSize: pageSize
      });
      
      // Add sentiment filter if not 'all'
      if (sentimentFilter !== 'all') {
        filterParams.append('sentiment', sentimentFilter);
      }
      
      // Add emotion filter if not 'all'
      if (emotionFilter !== 'all') {
        filterParams.append('emotion', emotionFilter);
      }
      
      // Add urgency filter if not 'all'
      if (urgencyFilter !== 'all') {
        filterParams.append('urgency', urgencyFilter);
      }
      
      // Add date range if not 'all'
      if (dateFilter !== 'all') {
        if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
          filterParams.append('date_filter', 'custom');
          filterParams.append('custom_start_date', customDateRange.start);
          filterParams.append('custom_end_date', customDateRange.end);
        } else {
          filterParams.append('date_filter', dateFilter);
        }
      }
      
      const response = await api.get(`/api/ai/review-analysis-filtered?${filterParams.toString()}`);
      
      if (response.data.success) {
        const analyses = response.data.analyses || [];
        const pagination = response.data.pagination || {};
        const meta = response.data.meta || {};
        
        console.log(`📄 Loaded ${analyses.length} reviews for page ${currentPage}`);
        console.log(`📊 Filter results:`, {
          total_in_database: meta.total_in_database,
          total_after_filters: meta.total_after_filters,
          showing: meta.showing
        });
        
        // Server handles all filtering - no client-side filtering needed
        console.log('🔍 Server applied all filters:', {
          total_in_database: meta.total_in_database,
          total_after_filters: meta.total_after_filters,
          showing: meta.showing
        });
        
        setReviewDetailsData(analyses);
        setTotalPages(pagination.totalPages || 1);
        setTotalAnalyses(pagination.total || analyses.length);
        
        // Debug the data
        if (analyses.length > 0) {
          console.log('🔍 Review details sample:', {
            first_review: analyses[0].reviews?.reviewer_name || 'Missing',
            urgent_count: analyses.filter(a => a.urgency_flag).length,
            has_review_data: analyses.filter(a => a.reviews).length
          });
        }
      }
    } catch (error) {
      console.error('Error fetching review details:', error);
      toast.showError('Failed to fetch review details');
    } finally {
      setLoadingReviews(false);
    }
  };

  const changePage = async (newPage) => {
    if (loadingReviews || newPage === currentPage || newPage < 1 || newPage > totalPages) return;
    
    console.log('🔍 Changing page from', currentPage, 'to', newPage);
    setCurrentPage(newPage);
    // fetchReviewDetails will be called by useEffect
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'reviews' && reviewDetailsData.length === 0) {
      fetchReviewDetails();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const loadMoreAnalyses = async () => {
    if (loadingMore || currentPage >= totalPages) return;
    
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      console.log(`🔄 Loading page ${nextPage} of ${totalPages}...`);
      
      const response = await api.get(`/api/ai/review-analysis?branch_id=${selectedBranch}&page=${nextPage}&pageSize=${pageSize}&limit=${pageSize}`);
      
      if (response.data.success) {
        const newAnalyses = response.data.analyses || [];
        const pagination = response.data.pagination || {};
        
        // Append new analyses to existing ones
        setReviewAnalyses(prev => [...prev, ...newAnalyses]);
        setCurrentPage(pagination.page || nextPage);
        
        console.log(`✅ Loaded ${newAnalyses.length} more analyses (total: ${reviewAnalyses.length + newAnalyses.length})`);
      }
    } catch (error) {
      console.error('Error loading more analyses:', error);
      toast.showError('Failed to load more reviews');
    } finally {
      setLoadingMore(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchBranchData = async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    try {
      // Fetch all the data we need in parallel
      const [
        unanalyzedResponse,
        analyzedResponse,
        analyzableResponse,
        totalReviewsResponse
      ] = await Promise.all([
        api.get(`/api/ai/unanalyzed-count?branch_id=${selectedBranch}`),
        api.get(`/api/ai/analyzed-count?branch_id=${selectedBranch}`),
        api.get(`/api/ai/analyzable-count?branch_id=${selectedBranch}`),
        api.get(`/api/reviews?branchIds=${selectedBranch}&limit=1`)
      ]);

      // Get the real counts
      const totalReviews = totalReviewsResponse.data.total || 0;
      const unanalyzedCount = unanalyzedResponse.data.count || 0; // All pending reviews
      const analyzedCount = analyzedResponse.data.count || 0; // Completed reviews
      const analyzableCount = analyzableResponse.data.count || 0; // Pending reviews with text
      
      // Debug logging
      console.log('🔍 API Response Debug:', {
        unanalyzedResponse: unanalyzedResponse.data,
        analyzedResponse: analyzedResponse.data,
        analyzableResponse: analyzableResponse.data,
        totalReviewsResponse: totalReviewsResponse.data
      });
      console.log('🔍 Calculated Counts:', {
        totalReviews,
        unanalyzedCount,
        analyzedCount,
        analyzableCount
      });
      
      // Calculate empty text reviews (unanalyzed - analyzable)
      const emptyTextCount = unanalyzedCount - analyzableCount;

      // Calculate completion percentage based on total reviews
      const completionPercentage = totalReviews > 0 ? ((analyzedCount / totalReviews) * 100).toFixed(1) : 0;

      // Update branch summary with real data
      // eslint-disable-next-line no-unused-vars
      const summaryData = {
        total_reviews: totalReviews,
        analyzed_reviews: analyzedCount,
        unanalyzed_count: analyzableCount, // Show only analyzable reviews
        skipped_reviews: emptyTextCount,
        analyzable_reviews: analyzableCount + analyzedCount,
        analysis_completion_percentage: completionPercentage
      };
      // Branch summary data is now stored in overviewData

      // Fetch review analyses for display and calculate stats
      console.log('🔍 fetchBranchData - Making API call to review-analysis for branch:', selectedBranch);
      
      // Fetch ALL reviews at once - no pagination limitation
      const maxLimit = Math.max(10000, totalAnalyses * 2); // Ensure we get all reviews
      console.log(`🔍 Fetching ALL reviews: limit=${maxLimit} (total analyses: ${totalAnalyses})`);
      
      // Fetch all reviews in one go
      const reviewsResponse = await api.get(`/api/ai/review-analysis?branch_id=${selectedBranch}&limit=${maxLimit}`);
      console.log('🔍 fetchBranchData - API response status:', reviewsResponse.status);
      console.log('🔍 fetchBranchData - API response data:', reviewsResponse.data);
      if (reviewsResponse.data.success) {
        const analyses = reviewsResponse.data.analyses || [];
        const pagination = reviewsResponse.data.pagination || {};
        const meta = reviewsResponse.data.meta || {};
        
        console.log('🔍 fetchBranchData - Raw analyses from API:', analyses.length);
        console.log('🔍 fetchBranchData - Pagination info:', pagination);
        console.log('🔍 fetchBranchData - Meta info:', meta);
        
        // COMPREHENSIVE DEBUGGING - Let's see what's actually in the data
        console.log('🔍 ===== DETAILED DEBUGGING START =====');
        console.log('🔍 Total analyses received:', analyses.length);
        
        if (analyses.length > 0) {
          const firstAnalysis = analyses[0];
          console.log('🔍 First analysis object:', firstAnalysis);
          console.log('🔍 First analysis keys:', Object.keys(firstAnalysis));
          console.log('🔍 First analysis.reviews:', firstAnalysis.reviews);
          console.log('🔍 First analysis.reviews type:', typeof firstAnalysis.reviews);
          console.log('🔍 First analysis.reviews keys:', firstAnalysis.reviews ? Object.keys(firstAnalysis.reviews) : 'N/A');
          
          if (firstAnalysis.reviews) {
            console.log('🔍 Review data details:');
            console.log('  - reviewer_name:', firstAnalysis.reviews.reviewer_name);
            console.log('  - stars:', firstAnalysis.reviews.stars);
            console.log('  - published_at_date:', firstAnalysis.reviews.published_at_date);
            console.log('  - review_text:', firstAnalysis.reviews.review_text ? 'Present' : 'Missing');
            console.log('  - branch_id:', firstAnalysis.reviews.branch_id);
          } else {
            console.log('🔍 ❌ NO REVIEW DATA FOUND for first analysis');
          }
          
          // Check a few more analyses
          analyses.slice(0, 3).forEach((analysis, index) => {
            console.log(`🔍 Analysis ${index + 1}:`, {
              id: analysis.id,
              review_id: analysis.review_id,
              branch_id: analysis.branch_id,
              has_reviews: !!analysis.reviews,
              reviews_type: typeof analysis.reviews,
              reviewer_name: analysis.reviews?.reviewer_name || 'MISSING',
              stars: analysis.reviews?.stars || 'MISSING',
              published_date: analysis.reviews?.published_at_date || 'MISSING',
              sentiment: analysis.sentiment,
              detected_emotion: analysis.detected_emotion,
              topics: analysis.topics?.length || 0
            });
          });
        }
        console.log('🔍 ===== DETAILED DEBUGGING END =====');
        
        // Update pagination state - but we're showing ALL data
        setTotalPages(1); // Only one page since we're showing all data
        setTotalAnalyses(analyses.length); // Total analyses we actually have
        setCurrentPage(1); // Always page 1 since we show all
        setPageSize(analyses.length); // Page size = total data
        // Pagination is disabled since we show all data
        
        // Check for null reviews in the data
        const nullReviews = analyses.filter(a => a.reviews === null);
        console.log('🔍 fetchBranchData - Analyses with null reviews:', nullReviews.length);
        if (nullReviews.length > 0) {
          console.log('🔍 fetchBranchData - First null review analysis:', nullReviews[0]);
        }
        
        console.log('🔍 fetchBranchData - Setting reviewAnalyses state with:', analyses.length, 'analyses');
        setReviewAnalyses(analyses);
        
        // Calculate statistics from ALL data (not paginated)
        const sentimentScores = analyses.map(a => a.sentiment_score).filter(score => score !== null && score !== undefined);
        const avgSentiment = sentimentScores.length > 0 
          ? sentimentScores.reduce((sum, score) => sum + score, 0) / sentimentScores.length 
          : 0;
        
        const urgentCount = analyses.filter(a => a.urgency_flag === true).length;
        
        console.log(`🔍 Statistics from ALL ${analyses.length} analyses:`);
        console.log(`  - Urgent reviews: ${urgentCount}`);
        console.log(`  - Average sentiment: ${avgSentiment.toFixed(2)}`);
        console.log(`  - Sentiment scores: ${sentimentScores.length}`);
        
        // Branch summary data is now stored in overviewData
      }

    } catch (error) {
      console.error('Error fetching branch data:', error);
      toast.showError('Failed to fetch branch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsightProfiles = async () => {
    if (!selectedBranch) return;
    
    try {
      const response = await api.get(`/api/ai/insight-profiles?branch_id=${selectedBranch}`);
      const data = response.data;
      if (data.success) {
        setInsightProfiles(data.profiles || []);
      }
    } catch (error) {
      console.error('Error fetching insight profiles:', error);
    }
  };


  // eslint-disable-next-line no-unused-vars
  const analyzeUnanalyzedReviews = async () => {
    if (!selectedBranch) return;
    
    setLoading(true);
    setAnalysisProgress({ 
      current: 0, 
      total: 0, 
      status: 'Finding unanalyzed reviews...',
      batch: 0,
      totalBatches: 0,
      errors: 0
    });
    
    try {
      const branchName = branches.find(b => b.id === selectedBranch)?.name || 'selected branch';
      toast.showInfo(`Starting AI analysis for ${branchName}...`, 3000);
      
      // First, get the count of analyzable reviews (pending with text)
      const countResponse = await api.get(`/api/ai/analyzable-count?branch_id=${selectedBranch}`);
      const countData = countResponse.data;
      
      if (countData.success && countData.count > 0) {
        const totalToAnalyze = countData.count;
        setAnalysisProgress({ 
          current: 0, 
          total: totalToAnalyze, 
          status: `Preparing to analyze ${totalToAnalyze} reviews...`,
          batch: 0,
          totalBatches: Math.ceil(totalToAnalyze / 500),
          errors: 0
        });
        
        // Start the analysis (non-blocking)
        const response = await api.post('/api/ai/auto-analyze', {
          max_reviews: totalToAnalyze, // Process ALL reviews
          branch_id: selectedBranch
        });
        
        const data = response.data;
        if (data.success) {
          // Start polling for progress
          const progressInterval = setInterval(async () => {
            try {
              const progressResponse = await api.get(`/api/ai/analysis-progress/${selectedBranch}`);
              const progressData = progressResponse.data;
              
              if (progressData.success) {
                const progress = progressData.progress;
                setAnalysisProgress({
                  current: progress.current,
                  total: progress.total,
                  status: progress.status,
                  batch: progress.batch,
                  totalBatches: progress.totalBatches,
                  errors: progress.errors
                });
                
                // If analysis is complete, stop polling
                if (progress.isComplete) {
                  clearInterval(progressInterval);
                  
                  // Store last analysis run info
                  setLastAnalysisRun({
                    branch_id: selectedBranch,
                    total_reviews: progress.total,
                    successful_analyses: progress.current,
                    failed_analyses: progress.errors,
                    batch_count: progress.totalBatches,
                    batch_size: Math.ceil(progress.total / progress.totalBatches),
                    completed_at: new Date().toISOString(),
                    branch_name: branchName
                  });
                  
                  toast.showSuccess(`Successfully analyzed ${progress.current} reviews!`);
                  
                  // Refresh data after analysis with longer delay
                  setTimeout(() => {
                    fetchOverviewData(); // Refresh overview data to show updated counts
                    if (activeTab === 'reviews') {
                      fetchReviewDetails(); // Also refresh review details if on that tab
                    }
                  }, 5000); // Increased delay to ensure analysis is fully saved
                  
                  // Reload the page after 8 seconds to show all updated data
                  setTimeout(() => {
                    window.location.reload();
                  }, 8000);
                  
                  // Keep progress visible indefinitely until user manually closes
                  // No timeout - let user see the final results
                }
              } else {
                // No progress data, analysis might not have started yet
                console.log('No progress data available yet...', progressData);
              }
            } catch (progressError) {
              console.error('Error fetching progress:', progressError);
            }
          }, 2000); // Poll every 2 seconds
          
          // No timeout - let analysis run until completion
          
        } else {
          throw new Error(data.error || 'Failed to trigger analysis');
        }
      } else {
        toast.showInfo('No new reviews found to analyze');
        setAnalysisProgress({ 
          current: 0, 
          total: 0, 
          status: 'No new reviews to analyze',
          batch: 0,
          totalBatches: 0,
          errors: 0
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error triggering analysis:', error);
      toast.showError(`Failed to start analysis: ${error.message}`);
      setAnalysisProgress({ 
        current: 0, 
        total: 0, 
        status: 'Analysis failed',
        batch: 0,
        totalBatches: 0,
        errors: 0
      });
      setLoading(false);
    }
  };

  const getDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    
    switch (range) {
      case 'yesterday':
        start.setDate(end.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'last_week':
        start.setDate(end.getDate() - 7);
        break;
      case 'last_month':
        start.setDate(end.getDate() - 30);
        break;
      case 'last_3_months':
        start.setDate(end.getDate() - 90);
        break;
      case 'last_6_months':
        start.setDate(end.getDate() - 180);
        break;
      case 'last_year':
        start.setDate(end.getDate() - 365);
        break;
      default:
        start.setDate(end.getDate() - 7);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const createInsightProfile = async () => {
    if (!selectedBranch || !insightForm.profile_name.trim()) {
      toast.showError('Please fill in all required fields');
      return;
    }

    const dateRange = insightForm.date_range === 'custom' 
      ? { start: insightForm.custom_start, end: insightForm.custom_end }
      : getDateRange(insightForm.date_range);

    try {
      const response = await api.post('/api/ai/insight-profiles', {
        profile_name: insightForm.profile_name,
        branch_ids: [selectedBranch],
        start_date: dateRange.start,
        end_date: dateRange.end,
        custom_criteria: insightForm.custom_criteria,
        ai_auto_detect: insightForm.ai_auto_detect
      });

      const data = response.data;
      if (data.success) {
        toast.showSuccess('Insight profile created! Processing will start shortly...');
        setShowCreateInsight(false);
        setInsightForm({
          profile_name: '',
          date_range: 'last_week',
          custom_start: '',
          custom_end: '',
          custom_criteria: [],
          ai_auto_detect: true
        });
        fetchInsightProfiles();
      } else {
        throw new Error(data.error || 'Failed to create insight profile');
      }
    } catch (error) {
      console.error('Error creating insight profile:', error);
      toast.showError(`Failed to create insight profile: ${error.message}`);
    }
  };

  const addCustomCriteria = () => {
    if (newCriteria.trim()) {
      setInsightForm(prev => ({
        ...prev,
        custom_criteria: [...prev.custom_criteria, newCriteria.trim()]
      }));
      setNewCriteria('');
    }
  };

  const removeCriteria = (index) => {
    setInsightForm(prev => ({
      ...prev,
      custom_criteria: prev.custom_criteria.filter((_, i) => i !== index)
    }));
  };

  const viewInsight = async (profileId) => {
    try {
      const response = await api.get(`/api/ai/insights?profile_id=${profileId}`);
      const data = response.data;
      if (data.success && data.insights.length > 0) {
        setCurrentInsight(data.insights[0]);
        setActiveTab('insight-detail');
      } else {
        toast.showError('No insight data found for this profile');
      }
    } catch (error) {
      console.error('Error fetching insight:', error);
      toast.showError('Failed to fetch insight data');
    }
  };

  // Filter change handlers - trigger new API calls
  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = () => {
    console.log('🔍 Filter changed, fetching new data...');
    setCurrentPage(1); // Reset to first page when filters change
    // Clear current data to show loading
    setReviewDetailsData([]);
    fetchReviewDetails();
  };

  const clearFilters = () => {
    setDateFilter('all');
    setSentimentFilter('all');
    setEmotionFilter('all');
    setUrgencyFilter('all');
    setCustomDateRange({ start: '', end: '' });
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== 'custom') {
      setCustomDateRange({ start: '', end: '' }); // Clear custom dates when switching away
    }
  };

  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => {
      const newRange = { ...prev, [field]: value };
      
      // Validate date range
      if (field === 'start' && newRange.end && value > newRange.end) {
        toast.showError('Start date cannot be later than end date');
        return prev;
      }
      if (field === 'end' && newRange.start && value < newRange.start) {
        toast.showError('End date cannot be earlier than start date');
        return prev;
      }
      
      return newRange;
    });
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return 'No Date';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateStr);
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date:', dateStr);
      return 'Invalid Date';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <Smile className="sentiment-icon positive" />;
      case 'negative': return <Frown className="sentiment-icon negative" />;
      default: return <Meh className="sentiment-icon neutral" />;
    }
  };

  const renderOverview = () => (
    <div className="analysis-overview">
      {/* Complete Statistics from overviewData */}
      <div className="stats-grid">
        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <h3>Reviews Analyzed</h3>
            <div className="stat-value">{overviewData.analyzed_reviews || 0}</div>
            <div className="stat-change">
              out of {overviewData.total_reviews || 0} total reviews
            </div>
            {overviewData.skipped_reviews > 0 && (
              <div className="stat-note">
                {overviewData.skipped_reviews} can't be analyzed (no text)
              </div>
            )}
          </div>
        </motion.div>

        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>Average Sentiment</h3>
            <div className="stat-value">
              {(overviewData.avg_sentiment_score || 0).toFixed(2)}
            </div>
            <div className="stat-change positive">Out of 1.00</div>
          </div>
        </motion.div>

        <motion.div className="stat-card urgent" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="stat-content">
            <h3>Urgent Issues</h3>
            <div className="stat-value">{overviewData.urgent_reviews || 0}</div>
            <div className="stat-change negative">Require attention</div>
          </div>
        </motion.div>

        <motion.div className="stat-card" whileHover={{ scale: 1.02 }}>
          <div className="stat-icon">
            <Brain size={24} />
          </div>
          <div className="stat-content">
            <h3>Completion</h3>
            <div className="stat-value">
              {Math.round(overviewData.analysis_completion_percentage || 0)}%
            </div>
            <div className="stat-change">Analysis progress</div>
          </div>
        </motion.div>
      </div>

      {/* Automated Analysis Notice */}
      <div className="analysis-action">
        <div className="action-description">
          <p>Reviews are automatically analyzed by the daily cron pipeline. New reviews are scraped and analyzed every night.</p>
          <div className="review-stats">
            <span className="stat-item">
              <Clock size={16} />
              {overviewData.unanalyzed_count || 0} reviews pending analysis (processed automatically)
            </span>
          </div>
        </div>
      </div>

      {/* Last Analysis Run Widget */}
      {lastAnalysisRun && (
        <div className="last-run-widget">
          <h4>Last Analysis Run</h4>
          <div className="run-details">
            <div className="run-item">
              <span className="label">Branch:</span>
              <span className="value">{lastAnalysisRun.branch_name}</span>
            </div>
            <div className="run-item">
              <span className="label">Completed:</span>
              <span className="value">{new Date(lastAnalysisRun.completed_at).toLocaleString()}</span>
            </div>
            <div className="run-item">
              <span className="label">Reviews Analyzed:</span>
              <span className="value">{lastAnalysisRun.successful_analyses}/{lastAnalysisRun.total_reviews}</span>
            </div>
            <div className="run-item">
              <span className="label">Batches:</span>
              <span className="value">{lastAnalysisRun.batch_count} batches of {lastAnalysisRun.batch_size}</span>
            </div>
            {lastAnalysisRun.failed_analyses > 0 && (
              <div className="run-item error">
                <span className="label">Failed:</span>
                <span className="value">{lastAnalysisRun.failed_analyses}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderInsightProfiles = () => (
    <div className="insight-profiles-section">
      <div className="section-header">
        <h2>Insight Profiles</h2>
        {user && user.role === 'admin' && (
          <motion.button
            className="action-btn primary"
            onClick={() => setShowCreateInsight(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus size={16} />
            Create New Insight
          </motion.button>
        )}
      </div>

      {/* Create Insight Form - Only show for admins */}
      <AnimatePresence>
        {showCreateInsight && user && user.role === 'admin' && (
          <motion.div
            className="create-insight-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="form-group">
              <label>Profile Name</label>
              <input
                type="text"
                placeholder="e.g., Weekly Customer Service Review"
                value={insightForm.profile_name}
                onChange={(e) => setInsightForm(prev => ({ ...prev, profile_name: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label>Date Range</label>
              <select
                value={insightForm.date_range}
                onChange={(e) => setInsightForm(prev => ({ ...prev, date_range: e.target.value }))}
              >
                <option value="yesterday">Yesterday</option>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="last_3_months">Last 3 Months</option>
                <option value="last_6_months">Last 6 Months</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {insightForm.date_range === 'custom' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={insightForm.custom_start}
                    onChange={(e) => setInsightForm(prev => ({ ...prev, custom_start: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={insightForm.custom_end}
                    onChange={(e) => setInsightForm(prev => ({ ...prev, custom_end: e.target.value }))}
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={insightForm.ai_auto_detect}
                  onChange={(e) => setInsightForm(prev => ({ ...prev, ai_auto_detect: e.target.checked }))}
                />
                Let AI automatically detect issues and topics
              </label>
            </div>

            <div className="form-group">
              <label>Custom Criteria (Optional)</label>
              <div className="criteria-input">
                <input
                  type="text"
                  placeholder="e.g., Customer service quality"
                  value={newCriteria}
                  onChange={(e) => setNewCriteria(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addCustomCriteria()}
                />
                <button type="button" onClick={addCustomCriteria}>Add</button>
              </div>
              {insightForm.custom_criteria.length > 0 && (
                <div className="criteria-list">
                  {insightForm.custom_criteria.map((criteria, index) => (
                    <span key={index} className="criteria-tag">
                      {criteria}
                      <button onClick={() => removeCriteria(index)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="action-btn secondary"
                onClick={() => setShowCreateInsight(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-btn primary"
                onClick={createInsightProfile}
              >
                Create & Analyze
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Insight Profiles List */}
      <div className="profiles-list">
        {insightProfiles.map((profile) => (
          <motion.div
            key={profile.id}
            className={`profile-card ${profile.status}`}
            whileHover={{ scale: 1.01 }}
          >
            <div className="profile-header">
              <h3>{profile.profile_name}</h3>
              <div className={`status-badge ${profile.status}`}>
                {profile.status === 'processing' && <Clock size={14} />}
                {profile.status === 'completed' && <CheckCircle size={14} />}
                {profile.status === 'failed' && <XCircle size={14} />}
                {profile.status}
              </div>
            </div>

            <div className="profile-details">
              <p><Calendar size={14} /> {formatDate(profile.start_date)} - {formatDate(profile.end_date)}</p>
              {profile.custom_criteria?.length > 0 && (
                <div className="criteria-preview">
                  <strong>Custom criteria:</strong> {profile.custom_criteria.join(', ')}
                </div>
              )}
            </div>

            {profile.status === 'completed' && (
              <motion.button
                className="view-insight-btn"
                onClick={() => viewInsight(profile.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Eye size={16} />
                View Insights
              </motion.button>
            )}

            {profile.status === 'processing' && (
              <div className="processing-indicator">
                <Brain size={16} className="spinning" />
                <span>Generating insights...</span>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {insightProfiles.length === 0 && (
        <div className="no-profiles">
          <Lightbulb size={48} />
          <h3>No insight profiles yet</h3>
          <p>Create your first insight profile to get AI-powered analysis of your reviews.</p>
        </div>
      )}
    </div>
  );

  const renderInsightDetail = () => (
    <div className="insight-detail-section">
      {currentInsight ? (
        <div className="insight-content">
          <div className="insight-header">
            <button
              className="back-btn"
              onClick={() => setActiveTab('insights')}
            >
              ← Back to Profiles
            </button>
            <h2>{currentInsight.profile_name || 'Insight Analysis'}</h2>
            <p>{formatDate(currentInsight.start_date)} - {formatDate(currentInsight.end_date)}</p>
          </div>

          {/* Overall Score */}
          <div className="overall-score-card">
            <div className="score-header">
              <h3>Overall Performance Score</h3>
              <div className={`score-badge ${currentInsight.overall_score >= 0.7 ? 'good' : currentInsight.overall_score >= 0.4 ? 'average' : 'poor'}`}>
                {(currentInsight.overall_score * 100).toFixed(0)}%
              </div>
            </div>
            <div className="score-details">
              <div className="trend">
                <TrendingUp size={16} />
                <span>Trend: {currentInsight.trend_direction || 'Unknown'}</span>
              </div>
              <div className="reviews-count">
                <BarChart3 size={16} />
                <span>{currentInsight.total_reviews_analyzed} reviews analyzed</span>
              </div>
            </div>
          </div>

          {/* Top Mistakes & Actions */}
          <div className="insights-grid">
            <div className="insight-card mistakes">
              <div className="card-header">
                <Target size={20} />
                <h3>Top Issues to Address</h3>
              </div>
              <div className="mistakes-list">
                {currentInsight.top_mistakes?.map((mistake, index) => (
                  <div key={index} className="mistake-item">
                    <div className="mistake-header">
                      <span className="mistake-rank">#{index + 1}</span>
                      <span className="mistake-frequency">{mistake.frequency} mentions</span>
                    </div>
                    <h4>{mistake.mistake}</h4>
                    <p>{mistake.description}</p>
                    <div className={`impact-level ${mistake.impact_level}`}>
                      {mistake.impact_level} impact
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="insight-card actions">
              <div className="card-header">
                <Lightbulb size={20} />
                <h3>Recommended Actions</h3>
              </div>
              <div className="actions-list">
                {currentInsight.recommended_actions?.map((action, index) => (
                  <div key={index} className="action-item">
                    <div className="action-header">
                      <span className={`priority-badge ${action.priority}`}>
                        {action.priority}
                      </span>
                      <span className="timeframe">{action.timeframe}</span>
                    </div>
                    <h4>{action.action}</h4>
                    <p>{action.expected_impact}</p>
                    <div className="resources">
                      <strong>Resources needed:</strong> {action.resources_needed}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Positive Highlights & Critical Areas */}
          <div className="highlights-grid">
            <div className="highlight-card positive">
              <div className="card-header">
                <Award size={20} />
                <h3>What's Working Well</h3>
              </div>
              <div className="highlights-list">
                {currentInsight.positive_highlights?.map((highlight, index) => (
                  <div key={index} className="highlight-item">
                    <CheckCircle size={16} />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="highlight-card critical">
              <div className="card-header">
                <AlertTriangle size={20} />
                <h3>Critical Areas</h3>
              </div>
              <div className="highlights-list">
                {currentInsight.critical_areas?.map((area, index) => (
                  <div key={index} className="highlight-item">
                    <XCircle size={16} />
                    <span>{area}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-insight">
          <Brain size={48} />
          <h3>No insight data found</h3>
          <button
            className="action-btn primary"
            onClick={() => setActiveTab('insights')}
          >
            Back to Profiles
          </button>
        </div>
      )}
    </div>
  );

  const renderReviews = () => (
    <div className="reviews-analysis-section">
      <div className="section-header">
        <h2>Review Analysis Details</h2>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        {/* Mobile Filter Toggle Button */}
        <div className="mobile-filter-toggle">
          <button 
            className="filter-toggle-btn"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Search size={20} />
            <span>{filtersExpanded ? 'Hide Filters' : 'Show Filters'}</span>
            {filtersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        
        {/* First Row - Main Filters */}
        <div className={`filter-row ${filtersExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="filter-group">
            <label>Date Range:</label>
            <select 
              value={dateFilter} 
              onChange={(e) => handleDateFilterChange(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_week">Last Week</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sentiment:</label>
            <select 
              value={sentimentFilter} 
              onChange={(e) => setSentimentFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Sentiments</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Emotion:</label>
            <select 
              value={emotionFilter} 
              onChange={(e) => setEmotionFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Emotions</option>
              <option value="joy">Joy</option>
              <option value="anger">Anger</option>
              <option value="frustration">Frustration</option>
              <option value="satisfaction">Satisfaction</option>
              <option value="disappointment">Disappointment</option>
              <option value="excitement">Excitement</option>
              <option value="concern">Concern</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Urgency:</label>
            <select 
              value={urgencyFilter} 
              onChange={(e) => setUrgencyFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Reviews</option>
              <option value="urgent">Urgent Only</option>
              <option value="normal">Normal Only</option>
            </select>
          </div>

          <motion.button
            className="clear-filters-btn"
            onClick={clearFilters}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <X size={16} />
            Clear Filters
          </motion.button>
        </div>

        {/* Second Row - Custom Date Range Fields */}
        {dateFilter === 'custom' && (
          <div className="custom-date-row">
            <div className="date-field">
              <label>From:</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => handleCustomDateChange('start', e.target.value)}
                className="date-input"
                placeholder="Start date"
                max={customDateRange.end || undefined}
              />
            </div>
            <div className="date-field">
              <label>To:</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => handleCustomDateChange('end', e.target.value)}
                className="date-input"
                placeholder="End date"
                min={customDateRange.start || undefined}
              />
            </div>
          </div>
        )}
      </div>

      <div className="reviews-list">
        {loadingReviews ? (
          <div className="loading-reviews">
            <RefreshCw size={24} className="spinning" />
            <span>Loading reviews...</span>
          </div>
        ) : reviewDetailsData.length === 0 ? (
          <div className="no-reviews-filtered">
            <Calendar size={48} />
            <h3>No reviews found</h3>
            <p>No reviews match your current filters. Try adjusting your date range or other filters.</p>
            <div className="filter-info">
              <p><strong>Current filters:</strong></p>
              <ul>
                {dateFilter !== 'all' && <li>Date: {dateFilter === 'custom' ? `${customDateRange.start} to ${customDateRange.end}` : dateFilter}</li>}
                {sentimentFilter !== 'all' && <li>Sentiment: {sentimentFilter}</li>}
                {emotionFilter !== 'all' && <li>Emotion: {emotionFilter}</li>}
                {urgencyFilter !== 'all' && <li>Urgency: {urgencyFilter}</li>}
              </ul>
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          reviewDetailsData.map((analysis) => (
          <motion.div
            key={analysis.id}
            className={`review-analysis-card ${analysis.urgency_flag ? 'urgent' : ''}`}
            layout
            whileHover={{ scale: 1.01 }}
          >
            <div className="review-header">
              <div className="reviewer-info">
                <h4>{analysis.reviews?.reviewer_name || (analysis.reviews ? 'Anonymous' : 'Review Data Missing')}</h4>
                <div className="review-meta">
                  <span className="stars">{'★'.repeat(analysis.reviews?.stars || 0)}</span>
                  <span>{formatDate(analysis.reviews?.published_at_date)}</span>
                </div>
              </div>
              <div className="analysis-badges">
                {getSentimentIcon(analysis.sentiment)}
                {analysis.urgency_flag && (
                  <div className="urgent-badge">
                    <AlertTriangle size={14} />
                    Urgent
                  </div>
                )}
              </div>
            </div>

            <div className="review-content">
              <p className="review-text">{analysis.reviews?.review_text}</p>
            </div>

            <div className="analysis-details">
              <div className="analysis-row">
                <strong>Emotion:</strong> 
                <span className="emotion-tag">{analysis.detected_emotion}</span>
              </div>
              
              {analysis.topics?.length > 0 && (
                <div className="analysis-row">
                  <strong>Topics:</strong>
                  <div className="topics-tags">
                    {analysis.topics.map((topic, index) => (
                      <span key={index} className="topic-tag">{topic}</span>
                    ))}
                  </div>
                </div>
              )}

              {analysis.pros?.length > 0 && (
                <div className="analysis-row">
                  <strong>Positives:</strong>
                  <ul className="pros-list">
                    {analysis.pros.map((pro, index) => (
                      <li key={index}>{pro}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.cons?.length > 0 && (
                <div className="analysis-row">
                  <strong>Issues:</strong>
                  <ul className="cons-list">
                    {analysis.cons.map((con, index) => (
                      <li key={index}>{con}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.urgency_flag && analysis.urgency_reason && (
                <div className="analysis-row urgent-reason">
                  <strong>Urgency Reason:</strong>
                  <span>{analysis.urgency_reason}</span>
                </div>
              )}
            </div>
          </motion.div>
          ))
        )}
      </div>

      {reviewDetailsData.length === 0 && !loadingReviews && (
        <div className="no-reviews">
          <BarChart3 size={48} />
          <h3>No review analyses found</h3>
          <p>Click "Analyze Reviews" to start analyzing your reviews with AI.</p>
        </div>
      )}

      {/* Data Summary - Show all data is loaded */}
      {reviewDetailsData.length > 0 && (
        <div className="pagination-controls">
          <div className="pagination-header">
            <div className="pagination-info">
              <span>Showing {reviewDetailsData.length} of {totalAnalyses} reviews</span>
              <span>Page {currentPage} of {totalPages}</span>
            </div>
            <div className="page-size-selector">
              <label>Reviews per page:</label>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }} 
                className="page-size-select"
              >
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <div className="pagination-buttons">
            <button 
              onClick={() => changePage(1)} 
              disabled={loadingReviews || currentPage === 1}
              className="pagination-btn"
            >
              First
            </button>
            <button 
              onClick={() => changePage(Math.max(1, currentPage - 1))} 
              disabled={loadingReviews || currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="page-info">{currentPage} / {totalPages}</span>
            <button 
              onClick={() => changePage(Math.min(totalPages, currentPage + 1))} 
              disabled={loadingReviews || currentPage >= totalPages}
              className="pagination-btn"
            >
              Next
            </button>
            <button 
              onClick={() => changePage(totalPages)} 
              disabled={loadingReviews || currentPage === totalPages}
              className="pagination-btn"
            >
              Last
            </button>
          </div>
          {loadingReviews && (
            <div className="pagination-loading">
              <RefreshCw size={16} className="spinning" />
              <span>Loading reviews...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="analysis-container">
      <div className="analysis-header">
        <div className="header-content">
          <h1>
            <Brain size={28} />
            AI Analysis Dashboard
          </h1>
          <p>AI-powered insights for {branches.find(b => b.id === selectedBranch)?.name || 'your branch'}</p>
        </div>
        
        <div className="header-controls">
          <div className="branch-selector">
            <label>Branch:</label>
            <select 
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
            >
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>{branch.name}</option>
              ))}
            </select>
          </div>
          
          <motion.button
            className="refresh-btn"
            onClick={() => {
              fetchOverviewData();
              if (activeTab === 'reviews') {
                fetchReviewDetails();
              }
            }}
            disabled={loading || loadingReviews}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RefreshCw size={16} className={(loading || loadingReviews) ? 'spinning' : ''} />
            Refresh
          </motion.button>
        </div>
      </div>

      <div className="analysis-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        {/* <button
          className={`tab ${activeTab === 'insights' ? 'active' : ''}`}
          onClick={() => setActiveTab('insights')}
        >
          <Lightbulb size={16} />
          Insights
        </button> */}
        <button
          className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => handleTabChange('reviews')}
        >
          <PieChart size={16} />
          Review Details
        </button>
      </div>

      <div className="analysis-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'insights' && renderInsightProfiles()}
            {activeTab === 'insight-detail' && renderInsightDetail()}
            {activeTab === 'reviews' && renderReviews()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Analysis;